/**
 * Rating math — spec section 2.
 *
 * Two independent ratings from one match:
 *   Deck Elo      — expectation from deck ratings only, so a deck's number
 *                   reflects the deck rather than whoever sleeved it up.
 *   Guildmate Elo — expectation from the guildmate gap PLUS the deck gap, so
 *                   deck rating acts as a handicap. Winning with the underdog
 *                   deck pays; grinding wins with your best deck barely moves
 *                   you.
 *
 * The identifiers here still say `pilot` — `pilotId`, `snapshot.pilots`, the
 * `/pilots` route — because they are the shape of every match ever stored, and
 * renaming them would mean rewriting recorded history to tidy up a label.
 * Nothing a person can read says it: the app calls them guildmates throughout,
 * including the errors this file throws.
 */

import type { MatchDeltas, MatchEntry } from './types.js';

export const BASE = 1200;

/**
 * How far a game can move a rating, and the reason it is not 24 any more.
 *
 * A winner in an even field gains `K / 2` however many people are at the table
 * — the pairwise scale of `1/(n-1)` is exactly what makes a pod worth the same
 * as a duel. At 24 that was twelve points, against about eight from turning up
 * and another ten for a couple of takedowns: winning the game was under half
 * of what winning the game paid, which is the wrong way round for a rating
 * whose entire job is to measure who wins.
 *
 * At 32 it is sixteen, or twenty with the margin multiplier behind it, and the
 * result is comfortably the largest thing on the night. The cost is that a
 * rating is now a third more sensitive to a single game — real, in a format
 * this swingy, and the lesser of the two problems: a bonus economy that
 * outweighs the result is not noisy, it is measuring the wrong thing.
 */
export const K_STANDARD = 32;
/** Spec 2.5 — first games do most of the work of finding the right level. */
export const K_PLACEMENT = 48;
export const PLACEMENT_GAMES = 5;

export function expected(diff: number): number {
  return 1 / (1 + Math.pow(10, -diff / 400));
}

/**
 * Where a deck's rating starts, given its bracket.
 *
 * A bracket is a claim, and the rating is a measurement — so the bracket is
 * used as a starting position and nothing else. It is never added to the
 * expected score. If it were, a deck's rating would drift towards "good for its
 * bracket" and stop answering the question the whole app is built on, which is
 * whether the deck is strong. Seeding says "we already know roughly where this
 * belongs"; the games then say whether that was true, and can say otherwise.
 *
 * The gaps are uneven because the brackets are. Beating the next bracket up is
 * hardest around the middle of the scale and easiest at the bottom:
 *
 *   I  -> II    70 points   ~60% for the favourite
 *   II -> III  180 points   ~74%
 *   III-> IV   140 points   ~69%
 *   IV -> V     90 points   ~63%
 *
 * Centred on III, so an unbracketed deck and a mid-bracket deck both start at
 * BASE and neither is advantaged by leaving the field blank.
 */
export const POWER_LEVEL_SEED: Record<1 | 2 | 3 | 4 | 5, number> = {
  1: BASE - 250,
  2: BASE - 180,
  3: BASE,
  4: BASE + 140,
  5: BASE + 230,
};

/** Unbracketed decks start at BASE: no claim, no prior. */
export function seedElo(powerLevel: 1 | 2 | 3 | 4 | 5 | null | undefined): number {
  return powerLevel ? POWER_LEVEL_SEED[powerLevel] : BASE;
}

/**
 * The part of a deck's rating it has actually earned, for handicapping a person.
 *
 * A deck's rating starts as a claim. The bracket seed above is a prior, chosen
 * from a dropdown by whoever registered it, and nothing has tested it yet — but
 * a guildmate's expectation reads the deck gap in full from the deck's very
 * first game, so the claim pays out immediately. Declaring a deck a bracket
 * lower starts it 180 points down, which is a 74/26 line before anybody
 * shuffles, and winning against a line like that pays like an upset.
 *
 * That is not a hypothetical. Over one session of eight games, five of them on
 * freshly registered bracket-II decks against established bracket-III ones, the
 * two guildmates went 4-4 and the ratings diverged by 87 points. Replaying that
 * night with this function in place brings it to 31.
 *
 * So the deck's distance from BASE is weighted by how much of it the deck has
 * played for: nothing on debut, all of it by the fifth game, the same five that
 * decide when any other rating stops being provisional. Nobody was gaming it —
 * the brackets were honest — but a self-reported number that pays real rating
 * and cannot be checked is the wrong shape whatever the intent behind it.
 *
 * Deliberately **not** used for the deck ladder itself. A deck's own rating has
 * to be free to travel from its seed to wherever its results put it, and
 * shrinking the gap it is measured against would be telling it to stay put.
 * The permanent `bracketHandicap` is untouched too: it is small, bounded and
 * explicit, and it exists precisely because the seed washes out.
 */
export function provenElo(elo: number, placementGames: number): number {
  return BASE + (elo - BASE) * Math.min(1, placementGames / PLACEMENT_GAMES);
}

export function kFor(placementGames: number): number {
  return placementGames < PLACEMENT_GAMES ? K_PLACEMENT : K_STANDARD;
}

/**
 * Ranks — one per hundred rating points, which is the band the ring already
 * draws. Fill the ring, gain a rank.
 *
 * Anchored so a new member starts at Anointed rather than in the middle. The
 * obvious layout put BASE in the centre with as much ladder below as above, and
 * the only story that offers somebody on their first night is decline. Two
 * ranks below the start is enough to make a bad run visible without being the
 * whole map; the five above are where a season actually goes.
 *
 * Evenly spaced on purpose, even though the top of the ladder is meant to be
 * much harder to reach than the bottom. The difficulty belongs in the progress
 * bonus, which pays less and less the higher a rating gets, and putting it in
 * both places would charge for it twice — a rank would be worth more points
 * *and* those points would be slower to earn. One rank is one hundred, and
 * whether that is a fortnight or a year is the curve's business.
 */
/** A rank is a hundred rating points wide — the band the result ring draws. */
export const RANK_BAND = 100;

/**
 * How full the ring is at this rating: 0 at the start of a rank, 1 at the end.
 *
 * Reads the real band rather than taking a modulus. It used to be
 * `elo % 100 / 100`, which was the same answer while every rank was a hundred
 * wide and silently the wrong one afterwards — the ring would fill three times
 * over inside Paragon.
 */
export const bandProgress = (elo: number): number => rankFor(elo).progress;

/** How the halo is drawn. Ornament carries rank where colour runs out. */
export type Halo = 'broken' | 'thin' | 'solid' | 'double' | 'radiant';

/**
 * Sanctity, drawn as a halo.
 *
 * A title ladder has to be learned — nobody knows on sight whether a Banneret
 * outranks a Marshal — so the rank is carried by a mark rather than a word.
 * Hue separates the lower ranks and ornament separates the upper ones, which is
 * the only way this scales: past silver and gold there are no distinguishable
 * colours left, but there is always another ring to add.
 *
 * A halo rather than an invented badge because the app was already drawing a
 * ring round everybody's face. The shape was there before the ladder was.
 */
export const RANKS: readonly {
  floor: number;
  name: string;
  color: string;
  halo: Halo;
  /** The top rank alone gets light coming off it. */
  glow?: boolean;
}[] = [
  { floor: -Infinity, name: 'Fallen', color: '#8a8496', halo: 'broken' },
  { floor: 1100, name: 'Mortal', color: '#cfc7b8', halo: 'thin' },
  { floor: 1200, name: 'Anointed', color: '#d8993c', halo: 'solid' },
  { floor: 1300, name: 'Blessed', color: '#79b4e8', halo: 'solid' },
  { floor: 1400, name: 'Extolled', color: '#ccd4e2', halo: 'double' },
  { floor: 1500, name: 'Sublime', color: '#e2b23e', halo: 'double' },
  { floor: 1600, name: 'Ascended', color: '#f3cd55', halo: 'radiant' },
  { floor: 1700, name: 'Paragon', color: '#ffffff', halo: 'radiant', glow: true },
];

/** Levels inside a rank, a third of a band each. Counted down: I is the top. */
export const RANK_LEVELS = 3;

/**
 * The bottom rank has no floor and the top has no ceiling, so both borrow the
 * width of the rank next to them. Otherwise the one place a fall genuinely
 * hurts and the one place a climb genuinely counts would be the two with
 * nothing to show for it.
 *
 * Borrowed rather than fixed, because the bands are no longer all the same
 * size: a hundred-wide Paragon would fill its ring three times over before
 * anybody got near the next thing, if there were one.
 */
function openBand(index: number): number {
  const above = RANKS[index + 1]?.floor;
  const below = RANKS[index - 1]?.floor;
  const here = RANKS[index]!.floor;
  // The bottom rank takes the width of the one above it; the top takes the
  // width of the one below.
  if (!Number.isFinite(here)) {
    const next = RANKS[index + 2]?.floor;
    return above !== undefined && next !== undefined ? next - above : RANK_BAND;
  }
  return below !== undefined && Number.isFinite(below) ? here - below : RANK_BAND;
}

export interface Rank {
  name: string;
  color: string;
  halo: Halo;
  glow: boolean;
  /** Position in the ladder, 0 at the bottom. */
  index: number;
  /** 1 at the top of the rank, 3 at the bottom — the way ladders count. */
  level: number;
  /** "Garnet II". */
  label: string;
  /** Where this rank starts. Notional for the bottom one. */
  floor: number;
  /** Where the next one starts, or null at the top. */
  next: number | null;
  /** How far through this rank, 0 to 1. */
  progress: number;
}

/**
 * How far below a rank's floor a single result can drop you and still leave the
 * rank standing.
 *
 * Ten, down from thirty, because the job changed. While grace was a buffer you
 * could sit inside indefinitely, this number *was* the rule and thirty points
 * bought about two ordinary games. Now that the next game which loses ground
 * takes the rank, all this decides is which falls are bad enough to skip the
 * reprieve — and against that question thirty was so far out it never fired.
 *
 * Measured, not guessed. A single game at these constants pays out roughly:
 *
 *   duel, even, established        -11    ordinary
 *   duel, even, blowout margin     -12
 *   duel, 200-point favourite      -22    bad
 *   duel, 400-point favourite      -31    disastrous
 *   duel, even, still in placement -20
 *   duel, 400 favourite, placement -50
 *
 * So an ordinary loss is about eleven, and thirty could only ever be reached
 * by a heavy favourite or somebody still being placed. Ten sits just under the
 * ordinary loss, which gives the rule the shape it was supposed to have: an
 * ordinary loss keeps the reprieve unless you were sitting right on the line,
 * and a genuinely heavy fall takes the rank there and then.
 *
 * Still asymmetric on purpose. Promotion happens the moment the floor is
 * crossed; demotion needs a fall and then a confirmation. In a league this
 * size the rating is noisy enough that a strict boundary would flip people
 * back and forth on nothing, and of the two ways to be wrong, briefly
 * over-ranking somebody is much the kinder.
 */
export const DEMOTION_GRACE = 10;

/** One rating and how it got there. `EloPoint` satisfies this structurally. */
export interface RatingStep {
  elo: number;
  /** The change that produced this rating. Zero for the opening point. */
  delta: number;
}

/**
 * The rank a rating history arrives at, with demotions held back.
 *
 * Needs the whole series rather than the current number, because "have they
 * given this rank up yet" is a question about the path and not the point. It is
 * replayed from the stored deltas rather than stored anywhere, so it cannot
 * drift out of step with the ratings it describes.
 *
 * Grace is one result, not a cushion. Falling below a rank's floor leaves the
 * rank standing but at risk, and **the next game that loses ground takes it**.
 * It used to be a flat thirty-point buffer you could sit inside indefinitely,
 * which made the dimmed halo a state rather than a warning — somebody could
 * wear a rank for a month without ever being back inside it.
 *
 * Losing ground rather than losing the game, because only one person wins a
 * game of Commander. Finishing second of four and gaining rating is not what
 * put anybody in grace and should not be what ends them; finishing last is.
 * In a duel the two readings are the same thing.
 */
export function rankThrough(history: readonly RatingStep[]): { index: number; held: boolean } {
  if (history.length === 0) return { index: 0, held: false };

  let index = rankFor(history[0]!.elo).index;
  /*
   * Whether they were already in grace *going into* this game.
   *
   * Carried across the loop rather than read from the current rating, because
   * the game that drops somebody below the floor is the one that puts them in
   * grace — it cannot also be the one that spends it, or grace would never
   * last a single game and the dimmed halo would never be seen by anybody.
   */
  let held = false;
  for (const step of history) {
    while (index + 1 < RANKS.length && step.elo >= RANKS[index + 1]!.floor) index++;
    // One result far enough below takes it outright.
    while (index > 0 && step.elo < RANKS[index]!.floor - DEMOTION_GRACE) index--;
    // Otherwise: already in grace, and this game lost ground. Reprieve spent.
    if (held && index > 0 && step.delta < 0 && step.elo < RANKS[index]!.floor) index--;
    held = index > 0 && step.elo < RANKS[index]!.floor;
  }

  const elo = history[history.length - 1]!.elo;
  return { index, held: Number.isFinite(RANKS[index]!.floor) && elo < RANKS[index]!.floor };
}

export function rankFor(elo: number, atIndex?: number): Rank {
  let index = 0;
  for (let i = RANKS.length - 1; i >= 0; i--) {
    if (elo >= RANKS[i]!.floor) {
      index = i;
      break;
    }
  }
  if (atIndex !== undefined) index = Math.min(RANKS.length - 1, Math.max(0, atIndex));

  const above = index + 1 < RANKS.length ? RANKS[index + 1]!.floor : null;
  const span = openBand(index);
  const floor = Number.isFinite(RANKS[index]!.floor)
    ? RANKS[index]!.floor
    : (above ?? RANK_BAND) - span;
  const ceiling = above ?? floor + span;

  const progress = Math.min(1, Math.max(0, (elo - floor) / (ceiling - floor)));
  // Thirds, counted down, so climbing runs III to II to I and the number
  // getting smaller is the good direction.
  const level = RANK_LEVELS - Math.min(RANK_LEVELS - 1, Math.floor(progress * RANK_LEVELS));

  return {
    name: RANKS[index]!.name,
    color: RANKS[index]!.color,
    halo: RANKS[index]!.halo,
    glow: RANKS[index]!.glow ?? false,
    index,
    level,
    label: `${RANKS[index]!.name} ${'I'.repeat(level)}`,
    floor,
    next: above,
    progress,
  };
}

export function isRanked(placementGames: number): boolean {
  return placementGames >= PLACEMENT_GAMES;
}

export function placementsRemaining(placementGames: number): number {
  return Math.max(0, PLACEMENT_GAMES - placementGames);
}

/**
 * Margin of victory — a multiplier on K, never a change to expected score.
 *
 * Softer than the spec's original curve, deliberately. The spec ranged 0.8–1.4;
 * this tops out at 1.25, so an untouched win is worth a quarter more than a
 * normal one rather than nearly half again. Margin in Commander is noisy — a
 * deck dead on board for three turns that stabilises at 4 life had a dominant
 * game recorded as a squeaker, and a combo deck wins at 40 every time without
 * the game ever being close. A gentle thumb on the scale survives that noise;
 * a heavy one just amplifies it.
 *
 * Fed by the winner's remaining life only. Commander damage deliberately does
 * not feed this: 21 commander damage is a different win condition, not a bigger
 * win.
 */
export const MARGIN_MIN = 0.9;
export const MARGIN_MAX = 1.25;

/**
 * The life total at which a win already counts as untroubled.
 *
 * Deliberately well short of the starting forty, and this is the anti-durdling
 * half of the rebalance. Remaining life is a genuine dominance signal at the
 * bottom — scraping home on 3 is a different game from cruising on 25 — and at
 * the top it stops measuring dominance and starts measuring *not having been
 * attacked*, which is exactly the thing a pod should not be paid for. Somebody
 * who sat behind a wall of defensive permanents while two other people fought
 * it out ends on forty, and the counter should not call that a blowout.
 *
 * So the curve tops out here: winning on 25 and winning on 40 are worth the
 * same, and everything the scale still discriminates between is a real
 * difference in how close the game was.
 */
export const MARGIN_LIFE_FULL = 25;
const LIFE_CURVE = Math.log2(1 + MARGIN_LIFE_FULL / 5);

/**
 * How dominant the win looked on life, 0 to 1.
 *
 * Logarithmic: the difference between winning at 4 and at 12 says far more than
 * the difference between 20 and 25, and a linear scale would claim otherwise.
 */
function lifeFactor(lifeRemaining: number): number {
  return Math.min(1, Math.max(0, Math.log2(1 + Math.max(0, lifeRemaining) / 5) / LIFE_CURVE));
}

/**
 * How dominant it looked on the clock, 0 to 1.
 *
 * Deliberately blunt: anything by turn four is as fast as this measures, and by
 * fourteen the clock has stopped saying anything. The points in between are the
 * only place a turn count is informative, and even there it is a weak signal.
 */
export const FAST_TURN = 4;
export const SLOW_TURN = 14;

function speedFactor(endTurn: number): number {
  return Math.min(1, Math.max(0, (SLOW_TURN - endTurn) / (SLOW_TURN - FAST_TURN)));
}

/**
 * Margin of victory — a multiplier on K, never a change to expected score.
 *
 * Softer than the spec's original curve, deliberately. The spec ranged 0.8–1.4;
 * this tops out at 1.25, so an untouched win is worth a quarter more than a
 * normal one rather than nearly half again. Margin in Commander is noisy — a
 * deck dead on board for three turns that stabilises at 4 life had a dominant
 * game recorded as a squeaker — and a gentle thumb on the scale survives that
 * noise where a heavy one just amplifies it.
 *
 * Life and the clock weigh the same. Both are needed for the maximum, which is
 * the point: a blowout is a game that ended early AND untroubled. Winning at 40
 * life on turn twelve is a game nobody could break through, not a game somebody
 * dominated.
 *
 * They used to be three-to-one in life's favour, and the rebalance is
 * deliberate. Each measures something other than dominance and they are wrong
 * in opposite directions, which is the case for splitting them evenly rather
 * than trusting either. Remaining life rewards not being attacked, and a pod
 * that pays for that is a pod where everybody sits behind a wall. Turn count
 * rewards being fast, which is archetype more than strength — a combo deck wins
 * on four every time it wins at all, and a grindy deck that beats it on eleven
 * did the harder thing. Balanced against each other, neither bias can run away
 * with it, and the whole band is only worth a few points a game anyway.
 *
 * The turn count is optional, and with it absent this is exactly the life-only
 * curve it has always been — so games logged after the fact are unaffected.
 *
 * Commander damage deliberately does not feed this: 21 commander damage is a
 * different win condition, not a bigger win.
 */
export const MARGIN_SPEED_WEIGHT = 0.5;

export function marginMultiplier(lifeRemaining: number, endTurn?: number): number {
  const life = lifeFactor(lifeRemaining);
  const blended =
    typeof endTurn === 'number' && endTurn > 0
      ? life * (1 - MARGIN_SPEED_WEIGHT) + speedFactor(endTurn) * MARGIN_SPEED_WEIGHT
      : life;
  return MARGIN_MIN + blended * (MARGIN_MAX - MARGIN_MIN);
}

export interface PilotRating {
  /** Everything: what the ladder shows and what a rank is read off. */
  elo: number;
  /**
   * How much of `elo` was paid for turning up rather than won.
   *
   * Subtracted back out before anything predicts anything — see `skillOf`.
   * Absent on ratings recorded before the split, where it is zero by
   * definition: nothing had been paid yet.
   */
  progress?: number;
  placementGames: number;
}

/**
 * The part of a rating that was won rather than earned by attending.
 *
 * **Every expectation in this file is computed from this, and every rating on
 * screen is the whole number.** That separation is what lets the league inflate
 * without the inflation lying to anybody.
 *
 * The problem it solves: one number cannot be both "how good the model thinks
 * you are" and "how much you have turned up", because the model reads the first
 * as skill. A guildmate joining in month six sits four hundred points under a
 * pool that has been collecting attendance points all season — they are not
 * four hundred points worse, they are four months newer — and a model that
 * cannot tell the difference writes them off in every prediction and then pays
 * them extravagantly for every win. Seeding them level would fix the arithmetic
 * by handing them the whole season's attendance for nothing, which is the same
 * unfairness pointed the other way.
 *
 * Splitting it means neither. They start level on skill, where they belong, and
 * climb the visible ladder by coming to game nights, which is the only way
 * anybody climbs it.
 *
 * It also makes the progress curve free. A bonus that pays whoever is behind
 * more than whoever is ahead is a restoring force, and while it was landing in
 * the rating the model used, it quietly squeezed the guildmate ladder — which
 * matters because pilot expectation adds a guildmate gap to a deck gap, and
 * squeezing one makes the other dominate. Paid into `progress` it cannot: the
 * skill ladder stays exactly the zero-sum Elo it always was, however steep the
 * curve gets.
 */
export function skillOf(rating: Pick<PilotRating, 'elo' | 'progress'>): number {
  return rating.elo - (rating.progress ?? 0);
}

export interface DeckRating {
  elo: number;
  peakElo: number;
  placementGames: number;
  /** Feeds the pilot handicap only; deck ratings measure decks. */
  powerLevel?: 1 | 2 | 3 | 4 | 5 | null;
}

/**
 * A small, permanent handicap for sitting behind the stronger bracket.
 *
 * The seed already puts a deck's bracket into its rating, but that washes out
 * as the rating converges on what the deck actually does. This does not: a
 * bracket gap is structural, and it does not stop being true because the
 * numbers have moved. Winning on the better deck should always prove a little
 * less than winning on the worse one.
 *
 * Small for a step or two, because one bracket is a real edge and not a
 * decided game. Three brackets apart is a different situation — the deck chose
 * the winner before anyone shuffled — so it doubles, and four triples.
 *
 *   1 bracket   25 points   ~54% expected
 *   2 brackets  50 points   ~57%
 *   3 brackets 150 points   ~70%
 *   4 brackets 300 points   ~85%
 *
 * Applied to the pilot rating only. Deck Elo has to keep answering "is this
 * deck strong", and a bracket term in its expectation would make it answer
 * "is this deck strong for its bracket" instead — a good III could never
 * out-rate the IVs it keeps beating, which is exactly what the seeding is
 * there to allow.
 */
export const BRACKET_HANDICAP_PER_STEP = 25;

function bracketEscalation(gap: number): number {
  if (gap >= 4) return 3;
  if (gap >= 3) return 2;
  return 1;
}

/** Positive when `mine` is the stronger bracket. Zero if either is unset. */
export function bracketHandicap(
  mine: 1 | 2 | 3 | 4 | 5 | null | undefined,
  theirs: 1 | 2 | 3 | 4 | 5 | null | undefined,
): number {
  if (!mine || !theirs) return 0;
  const diff = mine - theirs;
  const gap = Math.abs(diff);
  if (gap === 0) return 0;
  return Math.sign(diff) * gap * BRACKET_HANDICAP_PER_STEP * bracketEscalation(gap);
}

/** The slice of league state the math needs. Keyed by id. */
export interface RatingSnapshot {
  pilots: Record<string, PilotRating>;
  decks: Record<string, DeckRating>;
}

export interface ResolveInput {
  entries: MatchEntry[];
  winnerPilotId: string;
  /** Only read when margin of victory is enabled. */
  lifeRemaining?: Record<string, number>;
  /** The round the game ended on. Same gate; absent for games typed in later. */
  endTurn?: number;
  /**
   * Where everyone finished, as a rank per pilot. 1 is the winner; ties share a
   * number and skip the ones they consumed, so a four-player game with two
   * players tied second reads 1, 2, 2, 4.
   *
   * Absent for a game logged after the fact, and that absence is meaningful:
   * without it every loser draws with every other loser, which says only what
   * the game actually decided.
   */
  standing?: Record<string, number>;

  /**
   * Who knocked whom out, as killer keyed by victim.
   *
   * Only what the table confirmed. A player nobody credited — conceded, killed
   * by their own board wipe, walked into their own commander — is simply absent
   * rather than attributed to a guess, and pays nobody. Nobody appears as their
   * own killer.
   */
  takedowns?: Record<string, string>;

  /**
   * The odds the table was shown, frozen at kick-off and summing to one.
   *
   * Read only to pay the upset bonus. Absent on a game recorded without one,
   * which simply means nobody gets paid for beating expectations nobody wrote
   * down.
   */
  predicted?: Record<string, number>;
}

/**
 * A rating bonus for turning up, worth most at the bottom and least at the top.
 *
 * Plain Elo in a pool this size is a closed loop. The same three people trade
 * the same points back and forth forever, and an ordinary member's rating is a
 * flat line with noise on it — honest, and it never once reflects a season of
 * showing up. Twenty games is thirty or forty hours across a couple of months,
 * and that ought to be worth something.
 *
 * ### Why it decays against the rating and not against the pool
 *
 * The point of the decay is that a hundred points should cost more at 1400 than
 * at 1000 — that a rank near the top is a harder thing than a rank near the
 * bottom, and that whoever is behind is helped along as much as possible.
 * Neither is true of a curve drawn around wherever the guild currently sits:
 * there everybody rides up together earning the same average forever, every
 * rank costs the same twenty games, and Paragon arrives on a Tuesday.
 *
 * So it is keyed to the rating itself. At PROGRESS_FLOOR..PROGRESS_TOP over
 * PROGRESS_FROM..PROGRESS_CEILING that works out at:
 *
 *   1100 and below   5 a game
 *   1200             4.3            ~23 games to the next rank
 *   1300             3.7            ~27
 *   1400             3              ~33
 *   1500             2.3            ~43
 *   1600             1.7            ~60
 *   1700 and above   1              ~100
 *
 * The top of this curve was nine, and it had to come down. A four-player pod
 * paid about fifteen against places worth +16 / +5 / -5 / -16, so the two
 * middle seats of every pod were decided by having turned up rather than by
 * anything that happened in the game — second and third both went up, and one
 * guildmate finished fourteen games with a rating a hundred points above where
 * their results had put them.
 *
 * At five, the places decide the places. Results move a rating sixteen to
 * twenty-eight points a game depending on the size of the table, so a good run
 * arrives sooner and a bad one later; the column above is what turning up by
 * itself is worth, and it is now the smaller half of the night.
 *
 * It never reaches zero. A floor is what keeps the top of the ladder a slow
 * climb rather than a closed door — in a pool this small the last rank would
 * otherwise be unreachable for anybody who is merely the best of three.
 *
 * ### Why it is free
 *
 * A bonus that shrinks as you climb is a restoring force: it pays whoever is
 * behind more than whoever is ahead, every game, which pulls the ladder
 * together. That is inseparable from the feature — it *is* what "help the
 * low-rated player" means — and while this landed in the rating the model reads
 * it had a real cost, because pilot expectation adds a guildmate gap to a deck
 * gap and squeezing one lets the other dominate.
 *
 * It is paid into `progress` instead, which `skillOf` subtracts back out before
 * any expectation is computed. So the squeeze happens exactly where it was
 * wanted — the visible ladder, where catching up should feel possible — and
 * nowhere it was not. The skill ladder underneath is untouched zero-sum Elo,
 * and the curve above can be made as steep as it likes without the predictions
 * ever noticing.
 *
 * Guildmates only. A deck rating has to keep answering "is this deck strong",
 * and paying a deck for being played would make it answer "is this deck loved"
 * — a deck does not choose to show up, its owner does.
 */
/** What somebody at the bottom of the ladder earns per game. */
export const PROGRESS_TOP = 5;
/**
 * What somebody at Paragon earns, forever.
 *
 * One point. A hundred games to move a rank, which is a couple of years of
 * game nights — slow enough that the top of the ladder is genuinely climbed
 * rather than waited out, and not zero, so it is never shut.
 */
export const PROGRESS_FLOOR = 1;
/** Where the decay runs out and the floor takes over. */
export const PROGRESS_CEILING = 1700;
/** Where it starts decaying. At or below this, the full amount is paid. */
export const PROGRESS_FROM = 1100;

export function participationBonus(elo: number): number {
  const room = (PROGRESS_CEILING - elo) / (PROGRESS_CEILING - PROGRESS_FROM);
  return (
    PROGRESS_FLOOR + (PROGRESS_TOP - PROGRESS_FLOOR) * Math.min(1, Math.max(0, room))
  );
}

/*
 * Attendance does not read the table size, and the two attempts that made it
 * are worth keeping written down.
 *
 * The bonus is meant to pay for time served, and a game is not a unit of time:
 * a four-player pod runs ninety minutes and a duel runs thirty. So it was
 * multiplied by heads at the table — first by opponents faced, which paid a pod
 * about 23 against the 19 that finishing last cost and meant nobody at a full
 * pod could lose ground, and then by half that, which fixed the arithmetic and
 * not the idea.
 *
 * The idea was wrong. A pod is worth more than a duel because it holds more of
 * the thing being measured — three pairwise results instead of one — and that
 * belongs in `scale`, where it moves the ratings of whoever actually did well,
 * rather than in a payment every seat collects for being present. Paid as
 * attendance it landed hardest on exactly the seats it should not have: the
 * middle of a pod, where the result is worth a handful of points and the bonus
 * was worth fifteen.
 *
 * So it is one flat curve per game now, the same at every table, and `scale`
 * carries the size of the room. See the comment on `scale` in `resolveMatch`.
 */

/**
 * What a takedown is worth to whoever dealt it.
 *
 * Paid for knocking somebody out of a pod, and paid on nothing else: not on
 * winning, not on being the last one standing. Both of those are already worth
 * everything the pairwise exchange gives them, and a table where the points are
 * in eliminations is a table where people attack each other instead of durdling
 * until somebody assembles a combo.
 *
 * Scaled by the gap between the two seats — how far above or below you the
 * seat you removed was, and nothing else. Killing upward pays, killing
 * downward barely does, and two seats the odds cannot separate trade the
 * middle of the band either way.
 *
 * This is the third shape it has had and the first one worth reading at the
 * table. It scaled by the victim once, which made the table favourite a bounty
 * everybody piled onto. Then it scaled by the *killer* against the table
 * average, with a separate giant-killing bonus riding on top — and that is the
 * version that had to go. A four-player pod of guildmates sits inside sixty
 * rating points of each other, so every seat is within a whisker of the
 * average, and the screen offered +4.3, +4.0 and +4.2 for three completely
 * different targets. A number that never varies is not a decision.
 *
 * Against the seat you actually removed, the same table spans five of the nine
 * steps, because the gaps between seats are twice what any of them has to the
 * middle. Same evening, same ratings, a bounty that finally says something.
 *
 * Both halves of the seat count, which is the point of using strength rather
 * than rating alone — winning a fight on the weakest deck at the table is
 * worth more than winning it on the strongest, whoever is holding it.
 *
 * Paid into `progress` like the attendance bonus, and free for the same reason
 * — this is a restoring force too, and it cannot reach the predictions.
 *
 * Pods only. Heads-up, the only takedown available is the win itself.
 */
/*
 * Whole points, and never a fraction of one.
 *
 * The number is rounded here rather than on the way to the screen, so that
 * what a phone promises is exactly what the engine pays. The alternative —
 * paying 5.7 and displaying 6 — puts a rounding error between a rule and the
 * only place anybody can read it, and the whole reason this helper is shared
 * with the UI is that no such gap should exist.
 *
 * Nine down to one, centred on five. Deliberately wider than the old two-to-
 * six: the band is not a budget, it is a scale, and a scale that only ever
 * shows its middle may as well be a constant.
 *
 * Widening it barely moves the ladder, because the band is symmetric — what I
 * am paid for taking you out and what you are paid for taking me out always
 * add to ten, whoever we are. So a table pays five a kill on average whatever
 * the ratings do, against about four and a quarter before. Four three-handed
 * games pay a seat around thirteen from kills against forty-six from turning
 * up: three per cent more inflation for a number that finally varies.
 */
export const TAKEDOWN_MIN = 1;
/** What two seats of the same strength pay each other. */
export const TAKEDOWN_LEVEL = 5;
export const TAKEDOWN_MAX = 9;
/**
 * The gap that reaches the end of the band, in rating points.
 *
 * Sized against this guild rather than against Elo's own scale. Four hundred
 * is what Elo calls ten-to-one, and at four hundred the ends of the band would
 * never be reached by anybody here: the whole roster, decks included, lives
 * inside about a hundred and fifty points. A hundred and twenty puts the
 * extremes within reach of a real matchup while leaving the middle to the
 * pairings that genuinely are level.
 *
 * It will want raising, not lowering, as decks come out of placement — a
 * proven 1400 deck against a proven 1000 one is a gap this constant will
 * saturate long before it describes it.
 */
export const TAKEDOWN_FULL = 120;
/** Three-handed and up. In a duel, eliminating somebody *is* winning. */
export const TAKEDOWN_MIN_PLAYERS = 3;

/**
 * Winning a game the odds had written you off in.
 *
 * The prediction is already frozen at kick-off and stored on every match, kept
 * so the model can be marked against reality later — this is the first thing
 * that pays anybody for it.
 *
 * Elo settles the upset in skill already, and generously. This is not that: it
 * is the visible half, the thing that shows up on your card the morning after
 * you beat two people who were both meant to beat you. Because it is paid in
 * progress it cannot feed back into the odds it is measured against, which
 * matters more here than anywhere else — a bonus that moved the very number it
 * was scored from would be a loop.
 *
 * Scaled by how much of a longshot it was, against an even share of the pod
 * rather than a fixed percentage. A quarter is an ordinary chance in a
 * four-player game and an excellent one heads-up, so a flat threshold would pay
 * out constantly at one table size and never at another.
 */
/**
 * The largest single-event bonus in the league, and deliberately so.
 *
 * It is also the rarest by some distance. Nothing is paid at or above an even
 * share of the pod, so among three well-matched guildmates — where the odds sit
 * near a third apiece — this almost never fires at all. What moves the odds far
 * enough to trigger it is usually a lopsided deck matchup rather than a
 * lopsided table, which is exactly the game worth paying somebody for winning.
 *
 * Being rare is what lets it be big. A payout that fired every night at this
 * size would be most of the ladder.
 */
export const UPSET_MAX = 10;

export function upsetBonus(chance: number, players: number): number {
  if (!Number.isFinite(chance) || players < 2) return 0;
  const even = 1 / players;
  // Nothing at or above an even share; the full amount as the odds approach
  // hopeless. Smooth, so there is no threshold to sit just the wrong side of.
  return UPSET_MAX * Math.min(1, Math.max(0, (even - chance) / even));
}

/**
 * A seat's strength: the guildmate and the deck together, as the odds read it.
 *
 * Skill rather than the shown rating, so somebody who has been coming for a
 * year is not treated as the table's favourite on attendance alone. And the
 * deck only by what it has proven, for the same reason the guildmate handicap
 * reads it that way — the takedown bounty pays *more* to the seat that looks
 * weaker, so a rating a deck has not earned is a rating it gets paid for. Left
 * on the raw rating, registering a deck a bracket low was worth the top of the
 * band for eliminating people it had simply declared itself beneath.
 *
 * Takes the games rather than a pre-shrunk number so that no caller can hold it
 * wrongly: there is one way to build a seat strength and it is this one.
 */
export function seatStrength(pilotSkill: number, deckElo: number, deckGames: number): number {
  return pilotSkill + provenElo(deckElo, deckGames);
}

/**
 * The whole rule, in one line of arithmetic.
 *
 * Linear in the gap and clamped at both ends, which is the same shape the
 * giant-killing bonus had before it was folded in here — that one only ever
 * ran upward from level, and this runs both ways from it. Rounded to a whole
 * point, so this is literally the number that appears on a phone.
 *
 * The two halves are measured separately rather than as one symmetric swing.
 * They happen to be four points each today; if the band is ever reshaped so
 * that killing upward is worth more than killing downward is cheap, this keeps
 * paying the right amount without anybody noticing it had stopped.
 */
export function takedownBonus(killerStrength: number, victimStrength: number): number {
  const gap = victimStrength - killerStrength;
  const reach = Math.min(1, Math.max(-1, gap / TAKEDOWN_FULL));
  const swing = reach >= 0 ? TAKEDOWN_MAX - TAKEDOWN_LEVEL : TAKEDOWN_LEVEL - TAKEDOWN_MIN;
  return Math.round(TAKEDOWN_LEVEL + swing * reach);
}

/** A seat at a table, for working out what its bounties are worth. */
export interface Seat {
  pilotId: string;
  pilotSkill: number;
  deckElo: number;
  /** How many games the deck has played. See `seatStrength`. */
  deckGames: number;
}

/**
 * What each seat stands to earn at this table, worked out once.
 *
 * Keyed by the killer and then by the victim: `bountiesFor(field).get(me)![you]`
 * is what taking you out is worth to me. Per-target all the way down, because
 * there is no longer any such thing as what an elimination is worth to a seat
 * in general — that question had an answer only while the bounty was scaled by
 * the killer, and the answer was always about four.
 *
 * Exists so the counter can put real numbers on somebody's own screen. A rule
 * nobody can see the value of is not a rule anybody plays towards.
 *
 * Shared rather than reimplemented in the UI, because a screen that promised
 * four and paid three would be worse than a screen that promised nothing.
 * Whatever this returns is exactly what `resolveMatch` will pay.
 */
export function bountiesFor(field: readonly Seat[]): Map<string, Record<string, number>> {
  const strength = new Map(
    field.map((s) => [s.pilotId, seatStrength(s.pilotSkill, s.deckElo, s.deckGames)]),
  );
  // Heads-up there is nothing to pay: the only elimination available is the win.
  const pod = field.length >= TAKEDOWN_MIN_PLAYERS;

  return new Map(
    field.map((s) => {
      const on: Record<string, number> = {};
      if (pod) {
        for (const other of field) {
          if (other.pilotId === s.pilotId) continue;
          on[other.pilotId] = takedownBonus(
            strength.get(s.pilotId)!,
            strength.get(other.pilotId)!,
          );
        }
      }
      return [s.pilotId, on];
    }),
  );
}

export interface ResolveOptions {
  marginOfVictory?: boolean;
  /** The participation bonus above. Off means plain zero-sum Elo. */
  progressBonus?: boolean;
}

export interface ResolveResult {
  deltas: MatchDeltas;
  /** Ids that reach PLACEMENT_GAMES on this match — the reveal moment. */
  revealed: { pilots: string[]; decks: string[] };
}

export class EloError extends Error {}

function validate(input: ResolveInput, snapshot: RatingSnapshot): void {
  const { entries, winnerPilotId } = input;
  if (entries.length < 2 || entries.length > 4) {
    throw new EloError(`a match needs 2-4 players, got ${entries.length}`);
  }
  const pilotIds = new Set<string>();
  const deckIds = new Set<string>();
  for (const e of entries) {
    if (pilotIds.has(e.pilotId)) throw new EloError(`guildmate ${e.pilotId} entered twice`);
    if (deckIds.has(e.deckId)) throw new EloError(`deck ${e.deckId} entered twice`);
    pilotIds.add(e.pilotId);
    deckIds.add(e.deckId);
    if (!snapshot.pilots[e.pilotId]) throw new EloError(`unknown guildmate ${e.pilotId}`);
    if (!snapshot.decks[e.deckId]) throw new EloError(`unknown deck ${e.deckId}`);
  }
  if (!pilotIds.has(winnerPilotId)) {
    throw new EloError(`winner ${winnerPilotId} is not in the match`);
  }

  // A partial or self-contradicting standing is refused rather than ignored.
  // Silently falling back would score the game one way and show it another.
  const { standing } = input;
  if (standing) {
    for (const id of pilotIds) {
      const rank = standing[id];
      if (typeof rank !== 'number' || !Number.isFinite(rank) || rank < 1) {
        throw new EloError(`standing for ${id} is missing or not a rank`);
      }
    }
    for (const id of Object.keys(standing)) {
      if (!pilotIds.has(id)) throw new EloError(`standing names ${id}, who is not in the match`);
    }
    const best = Math.min(...[...pilotIds].map((id) => standing[id]!));
    if (standing[winnerPilotId] !== best) {
      throw new EloError('the winner is not first in the standing');
    }
    if ([...pilotIds].filter((id) => standing[id] === best).length > 1) {
      throw new EloError('the standing has more than one player in first');
    }
  }

  // Same rule as the standing: a takedown naming somebody who was not at the
  // table is refused rather than dropped, because a credit shown on the result
  // screen and silently worth nothing is the worst of both.
  const { takedowns } = input;
  if (takedowns) {
    for (const [victim, killer] of Object.entries(takedowns)) {
      if (!pilotIds.has(victim)) throw new EloError(`${victim} was taken down but was not playing`);
      if (!pilotIds.has(killer)) throw new EloError(`${killer} took somebody down but was not playing`);
      if (victim === killer) throw new EloError('nobody takes themselves down');
    }
  }
}

/**
 * Resolve one match into exact deltas.
 *
 * Multiplayer (spec 2.4) is resolved pairwise: each player is scored against
 * each other, and K is scaled by 1/sqrt(n-1) so a four-player game is worth
 * more than a duel without being worth three of them. See `scale` below.
 *
 * With a standing, a pair is decided by who finished higher. Without one, the
 * winner scores 1 against everybody and the losers all draw with each other —
 * which is not a shrug, it is the honest reading of a game where nobody wrote
 * down who went out when.
 *
 * This is where "second place should lose less" comes from, and it comes for
 * free rather than from a payout table: last place lost three exchanges, second
 * lost one and won two, and an underdog who outlasts the field can finish
 * second and still gain. Nothing had to be asserted about what a placement is
 * worth.
 *
 * K is per participant, not per match — a deck still in placements moves at 48
 * while its opponent moves at 24. That makes a match deliberately non-zero-sum,
 * which is the point: provisional ratings should converge faster than the
 * established ones they are measured against drift.
 */
/** One pair, decided by the standing if there is one and by the winner if not. */
function pairScore(a: string, b: string, input: ResolveInput): number {
  const rankA = input.standing?.[a];
  const rankB = input.standing?.[b];
  if (rankA !== undefined && rankB !== undefined) {
    if (rankA === rankB) return 0.5;
    return rankA < rankB ? 1 : 0;
  }
  if (a === input.winnerPilotId) return 1;
  if (b === input.winnerPilotId) return 0;
  return 0.5;
}

export function resolveMatch(
  input: ResolveInput,
  snapshot: RatingSnapshot,
  options: ResolveOptions = {},
): ResolveResult {
  validate(input, snapshot);

  const { entries, winnerPilotId } = input;
  const n = entries.length;
  /*
   * How much of K one pairing is allowed to move.
   *
   * A player at a table of n is scored against n-1 others, so this decides
   * what a pod is worth against a duel. It was 1/(n-1), which made every table
   * worth exactly one duel however many people were at it: the winner always
   * gained about sixteen, last place always lost about sixteen, and a
   * four-player pod split the middle into steps of five.
   *
   * That is one duel's worth of movement carrying three duels' worth of
   * evidence. A four-player game is three pairwise results — who beat whom,
   * three times over — and thinning each one to a third throws away most of
   * what the longest game of the night actually told us. It is also what made
   * the attendance bonus the biggest thing at a pod: the payment scaled with
   * the table and the result did not.
   *
   * The root of it instead. Evidence accumulates as the square root of how
   * much of it there is, which is the ordinary answer everywhere else, and it
   * gives a pod more weight than a duel without giving it three times as much:
   * a duel is unchanged, a three-player game moves 1.4x and a four-player game
   * 1.7x. Places at a full pod become +28 / +9 / -9 / -28, and the middle two
   * seats are decided by the game again.
   *
   * Deck ratings run through the same number, and for the same reason — a pod
   * says as much about the decks in it as about the people holding them.
   */
  const scale = 1 / Math.sqrt(n - 1);

  let mult = 1;
  if (options.marginOfVictory) {
    const winnerLife = input.lifeRemaining?.[winnerPilotId];
    if (typeof winnerLife === 'number') mult = marginMultiplier(winnerLife, input.endTurn);
  }

  const pilots: Record<string, number> = {};
  const decks: Record<string, number> = {};
  /** The part of each guildmate's change that was paid rather than won. */
  const progress: Record<string, number> = {};
  for (const e of entries) {
    pilots[e.pilotId] = 0;
    decks[e.deckId] = 0;
    progress[e.pilotId] = 0;
  }

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const a = entries[i]!;
      const b = entries[j]!;

      const scoreA = pairScore(a.pilotId, b.pilotId, input);
      const scoreB = 1 - scoreA;

      const pa = snapshot.pilots[a.pilotId]!;
      const pb = snapshot.pilots[b.pilotId]!;
      const da = snapshot.decks[a.deckId]!;
      const db = snapshot.decks[b.deckId]!;

      const dGap = da.elo - db.elo;
      // Skill, not the shown rating. Attendance points are in there too, and
      // the model must not read a year of turning up as being better at Magic.
      const pGap = skillOf(pa) - skillOf(pb);

      // Deck Elo — deck ratings only, and the ratings themselves, not the
      // proven part of them. A deck has to be able to travel from its seed.
      const deckExpA = expected(dGap);
      decks[a.deckId]! += kFor(da.placementGames) * mult * scale * (scoreA - deckExpA);
      decks[b.deckId]! += kFor(db.placementGames) * mult * scale * (scoreB - (1 - deckExpA));

      // Pilot Elo — handicapped by the deck gap, plus a standing penalty for
      // holding the stronger bracket that does not wash out as ratings move.
      // The handicap reads only what the decks have proven; see `provenElo`.
      const hGap =
        provenElo(da.elo, da.placementGames) - provenElo(db.elo, db.placementGames);
      const bGap = bracketHandicap(da.powerLevel, db.powerLevel);
      const pilotExpA = expected(pGap + hGap + bGap);
      pilots[a.pilotId]! += kFor(pa.placementGames) * mult * scale * (scoreA - pilotExpA);
      pilots[b.pilotId]! += kFor(pb.placementGames) * mult * scale * (scoreB - (1 - pilotExpA));
    }
  }

  /*
   * The progress bonus, once per match rather than once per pairing, and
   * outside the margin multiplier — it is paid for turning up, so a blowout
   * does not pay more of it than a squeaker.
   */
  /*
   * Both bonuses land in `progress` as well as in the rating.
   *
   * The rating is what the ladder shows, so they have to be in it. `progress`
   * is the running total of how much of that was paid rather than won, and
   * `skillOf` subtracts it back out before anything predicts anything — which
   * is what stops a season of attendance from reading as a season of getting
   * better, and what makes both curves below free to be as steep as they like.
   */
  /** Pays somebody, and remembers that it was paid rather than won. */
  const pay = (pilotId: string, amount: number) => {
    if (pilots[pilotId] === undefined || amount <= 0) return;
    pilots[pilotId]! += amount;
    progress[pilotId]! += amount;
  };

  /*
   * One switch over everything that inflates.
   *
   * A leader who turns this off wants the plain zero-sum ladder, and leaving
   * three other sources of progress running underneath a setting called
   * "progress bonus" would make that switch a lie.
   */
  if (options.progressBonus) {
    for (const e of entries) {
      pay(e.pilotId, participationBonus(snapshot.pilots[e.pilotId]!.elo));
    }

    /*
     * Beating what the table was told would happen.
     *
     * Paid on the frozen odds rather than recomputed ones: the ratings behind
     * them can move during the forty minutes a game takes, and what somebody
     * beat is what they were shown.
     */
    const chance = input.predicted?.[winnerPilotId];
    if (typeof chance === 'number') pay(winnerPilotId, upsetBonus(chance, n));

    /*
     * Takedowns, paid to whoever the table said dealt them.
     *
     * Pods only, and outside the margin multiplier for the same reason the
     * attendance bonus is: this is paid for a thing that happened, not for how
     * comfortably the game ended. Nothing is paid for the win itself — the
     * pairwise exchange already settles that.
     */
    if (n >= TAKEDOWN_MIN_PLAYERS && input.takedowns) {
      const strength = new Map(
        entries.map((e) => [
          e.pilotId,
          seatStrength(
            skillOf(snapshot.pilots[e.pilotId]!),
            snapshot.decks[e.deckId]!.elo,
            snapshot.decks[e.deckId]!.placementGames,
          ),
        ]),
      );
      for (const [victim, killer] of Object.entries(input.takedowns)) {
        // Both have to be at this table, and nobody takes themselves down.
        if (victim === killer) continue;
        if (pilots[killer] === undefined || strength.get(victim) === undefined) continue;
        // Scaled by the gap between the two of them, so killing upward pays
        // and killing downward barely does.
        pay(killer, takedownBonus(strength.get(killer)!, strength.get(victim)!));
      }
    }
  }

  // Placement counters advance once per match, not once per pairing.
  const revealed = { pilots: [] as string[], decks: [] as string[] };
  for (const e of entries) {
    const p = snapshot.pilots[e.pilotId]!;
    if (p.placementGames + 1 === PLACEMENT_GAMES) revealed.pilots.push(e.pilotId);
    const d = snapshot.decks[e.deckId]!;
    if (d.placementGames + 1 === PLACEMENT_GAMES) revealed.decks.push(e.deckId);
  }

  return { deltas: { pilots, decks, progress }, revealed };
}

/** Apply a resolved match to a snapshot in place. Mirror of revertMatch. */
export function applyMatch(snapshot: RatingSnapshot, deltas: MatchDeltas): void {
  for (const [id, delta] of Object.entries(deltas.pilots)) {
    const p = snapshot.pilots[id];
    if (!p) continue;
    p.elo += delta;
    // Tracked alongside so skill stays recoverable. Absent on matches from
    // before the split, where nothing had been paid and zero is correct.
    p.progress = (p.progress ?? 0) + (deltas.progress?.[id] ?? 0);
    p.placementGames += 1;
  }
  for (const [id, delta] of Object.entries(deltas.decks)) {
    const d = snapshot.decks[id];
    if (!d) continue;
    d.elo += delta;
    d.placementGames += 1;
    if (d.elo > d.peakElo) d.peakElo = d.elo;
  }
}

/**
 * Undo — spec 6.1, built first because someone will misclick the winner.
 *
 * Restores the exact stored deltas rather than recomputing, so an undo is a true
 * inverse even if the rating landscape has shifted. peakElo is the one thing that
 * cannot be inverted from deltas alone; the caller recomputes it from history.
 */
export function revertMatch(snapshot: RatingSnapshot, deltas: MatchDeltas): void {
  for (const [id, delta] of Object.entries(deltas.pilots)) {
    const p = snapshot.pilots[id];
    if (!p) continue;
    p.elo -= delta;
    p.progress = Math.max(0, (p.progress ?? 0) - (deltas.progress?.[id] ?? 0));
    p.placementGames = Math.max(0, p.placementGames - 1);
  }
  for (const [id, delta] of Object.entries(deltas.decks)) {
    const d = snapshot.decks[id];
    if (!d) continue;
    d.elo -= delta;
    d.placementGames = Math.max(0, d.placementGames - 1);
  }
}

export interface Contender {
  /**
   * The guildmate's **skill**, not the rating on their card.
   *
   * Callers pass `skillOf(pilot)`. Handing this the shown rating would make the
   * odds treat a season of attendance as a season of improvement, and give the
   * newest person at the table odds that say they cannot win.
   */
  pilotElo: number;
  deckElo: number;
  /**
   * How many games the deck has played, so the odds can discount a rating the
   * deck has not earned yet — exactly as `resolveMatch` does. See `provenElo`.
   *
   * Required rather than optional on purpose. Every caller has this to hand,
   * and a caller that quietly left it out would show a line the payout does
   * not agree with, which is the kind of wrong that never announces itself.
   */
  deckGames: number;
  powerLevel?: 1 | 2 | 3 | 4 | 5 | null;
}

/**
 * Predicted win chance for every seat, before a card is drawn.
 *
 * A seat's strength is the same quantity the pilot rating is judged against —
 * pilot plus deck plus its share of the bracket handicap — so the prediction
 * and the rating can never disagree about who was favoured. For two players
 * this reduces exactly to `expected()` of the gap, which is asserted by a test.
 *
 * Beyond two it is a Bradley-Terry spread: each seat weighted by 10^(strength
 * /400) and normalised. That is an estimate and not the model the rating uses,
 * because ratings resolve pairwise — in a pod, who attacks whom decides more
 * than any of this. It is honest about roughly who the underdog is and useless
 * for anything finer.
 */
export function winChances(field: readonly Contender[]): number[] {
  const n = field.length;
  if (n === 0) return [];
  if (n === 1) return [1];

  const strengths = field.map((seat, i) => {
    let handicap = 0;
    for (let j = 0; j < n; j++) {
      if (j !== i) handicap += bracketHandicap(seat.powerLevel, field[j]!.powerLevel);
    }
    // Halved because the pairwise expectation counts one gap between two seats,
    // and here both ends of that gap are being credited separately.
    return (
      seat.pilotElo + provenElo(seat.deckElo, seat.deckGames) + handicap / (n - 1) / 2
    );
  });

  // Shift before exponentiating; the raw strengths are around 2400.
  const best = Math.max(...strengths);
  const weights = strengths.map((s) => Math.pow(10, (s - best) / 400));
  const total = weights.reduce((a, b) => a + b, 0);
  return weights.map((w) => w / total);
}

/** Ratings display as integers (spec 10) but are stored at full precision. */
export function display(elo: number): number {
  return Math.round(elo);
}

/**
 * Spec 10 — a 60-point gap is meaningful, a 15-point gap is nothing. Used to
 * grey out differences the sample size cannot support.
 */
export function gapIsMeaningful(a: number, b: number): boolean {
  return Math.abs(a - b) >= 60;
}
